/* eslint-disable import/no-unused-modules */
import { useMemo } from "react";
import { IconsId } from "@silversea-ssc/design-system-icons";

import { shorexesGrouppedByCategory } from "./ShorexDrawer/groupped-shorexes";
import useLiveSanityContent from "src/ssc-api/useLiveSanityContent";
import { Language } from "src/utils/localization/languages/types";
import { PartialSanityImageType } from "@silversea-ssc/ui-components-react";
import { ShorexCode } from "src/page-components/cruise/itinerary/types";

export type SanityMicroContentExcursionsAccessibilityLevel = {
  _key: string;
  code: string;
  icon?: IconsId;
  content?: string;
  label?: string;
  shortContent?: string;
};

export type SanityMicroContentExcursionsActivityLevel = {
  _key: string;
  code: string;
  content?: string;
  label?: string;
  iconStep?: number;
};

export type SanityMicroContentExcursionsFeature = {
  _key: string;
  code: string;
  content?: string;
  label?: string;
  icon?: IconsId;
};

export type SanityMicroContentExcursionsSubcategory = {
  _key: string;
  code: string;
  content?: string;
  label?: string;
};

export type SanityMicroContentExcursions = {
  accessibilityLevels: SanityMicroContentExcursionsAccessibilityLevel[];
  activityLevels: SanityMicroContentExcursionsActivityLevel[];
  features: SanityMicroContentExcursionsFeature[];
  subcategories: SanityMicroContentExcursionsSubcategory[];
};

export type SanityShorexData = {
  _key?: string;
  activityLevel?: number;
  accessibility?: string;
  code?: string;
  features?: ShorexCode[];
  name?: string;
  shorexWebCode?: string;
  descriptionHtml?: string;
  noteHtml?: string;
  category?: string;
  subcategory?: string;
  image?: PartialSanityImageType;
};

export type SanityShorexesSelectionConfirmedShorex = {
  _key: string;
  shorexWebCode: string;
  duration?: number;
  price?: number;
  shorex: SanityShorexData | undefined;
};

export type SanityShorexesSelection = SanityShorexesSelectionConfirmedShorex[];

const shorexContentQueryProjection = `
  _key,
  activityLevel,
  category,
  subcategory,
  accessibility,
  code,
  features,
  name,
  shorexWebCode,
  descriptionHtml,
  noteHtml,
  image{
      asset->{
      _id,
      assetId,
      url,
      extension,
      metadata{dimensions}
      }
  }
`;

const shorexesSelectionQuery = `
 *[_type == "cruiseData" && cruiseCode in $cruiseCodes]{
   "confirmedShorexes": itinerary[] {
        confirmedShorexes[] {
            _key,
            duration,
            price,
            shorexWebCode,
            "shorex": *[_type == "shorex" && shorexWebCode == ^.shorexWebCode && isEligibleForShorexCredit == true && name != ""][0] {
                ${shorexContentQueryProjection}
            }
        }
    }.confirmedShorexes[]
}.confirmedShorexes[]|order(shorex.category asc, price desc)`;

export const useSanityShorexSelection = (
  cruiseCodes: string[],
  language: Language
) => {
  const { status, data } = useLiveSanityContent<
    SanityShorexesSelection | undefined
  >(shorexesSelectionQuery, {
    $cruiseCodes: cruiseCodes,
    $lang: language.sanityCode
  });

  const shorexGroups = useMemo(
    () => shorexesGrouppedByCategory(data?.result),
    [data?.result]
  );

  return { status, shorexGroups };
};

const shorexContentQuery = `
*[_type == "shorex" && shorexWebCode == $shorexWebCode]
    {
      ${shorexContentQueryProjection}
    }[0]
`;

export const useShorexContent = (shorexWebCode: string) => {
  return useLiveSanityContent<SanityShorexData>(shorexContentQuery, {
    $shorexWebCode: shorexWebCode
  });
};
